import axios from "axios";
import { createAxiosInstance } from "./utils/Axios";

const axiosInstance = createAxiosInstance();
const ASKNEWA_URL = process.env.REACT_APP_ASKNEWA_URL || 'http://localhost:8001';

// ─── GLOBAL HELPER ───────────────────────────────────────────
const api = {
  get: async (url, params = {}) => {
    const response = await axiosInstance.get(url, { params });
    return response.data;
  },
  post: async (url, body = {}) => {
    const response = await axiosInstance.post(url, body);
    return response.data;
  },
  put: async (url, body = {}) => {
    const response = await axiosInstance.put(url, body);
    return response.data;
  },
  patch: async (url, body = {}) => {
    const response = await axiosInstance.patch(url, body);
    return response.data;
  },
  delete: async (url) => {
    const response = await axiosInstance.delete(url);
    return response.data;
  }
};

// ─── AGENTS ──────────────────────────────────────────────────
export const getAgents = (query = {}) => api.get("/irp/agents", query);
export const applyAgent = (id) => api.post(`/irp/agents/${id}/apply`);
export const uninstallAgent = (id) => api.post(`/irp/agents/${id}/uninstall`);

// ─── INVOICES ────────────────────────────────────────────────
export const getInvoices = (leadId, params = {}) => api.get("/leads/invoices", { leadId, ...params });
export const getInvoiceById = (id) => api.get(`/leads/invoices/${id}`);
export const createInvoice = (data) => api.post("/leads/invoices", data);
export const updateInvoice = (id, data) => api.put(`/leads/invoices/${id}`, data);
export const deleteInvoice = (id) => api.delete(`/leads/invoices/${id}`);
export const updateInvoiceStatus = (id, status) => api.patch(`/leads/invoices/${id}/status`, { status });
export const sendInvoice = (id, payload = {}) => api.post(`/leads/invoices/${id}/send`, payload);
export const markInvoiceAsPaid = (id, payload = {}) => api.post(`/leads/invoices/${id}/mark-paid`, payload);
export const getInvoiceNumber = () => api.get("/leads/invoices");
export const viewInvoice = (id) => api.get(`/leads/invoices/${id}`);
export const getReceivedInvoices = () => api.get("/leads/invoices");
export const getSentInvoices = () => api.get("/leads/invoices", { status: "Sent" });
export const IrpgetInvoices = () => api.get("/leads/invoices");
export const uploadInvoicePdf = async (quoteId, formData) => {
  const { data } = await axiosInstance.post(
    `/leads/invoices/upload-pdf/${quoteId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return data;
};

export const getInvoicePdfLink = (quoteId) =>
  api.get(`/leads/invoices/pdf-link/${quoteId}`);
  
// ─── QUOTES ──────────────────────────────────────────────────
export const getQuotes = (leadId, params = {}) => api.get("/leads/quotes", { leadId, ...params });
export const getQuoteById = (id) => api.get(`/leads/quotes/${id}`);
export const createQuote = (data) => api.post("/leads/quotes", data);
export const updateQuote = (id, data) => api.put(`/leads/quotes/${id}`, data);
export const deleteQuote = (id) => api.delete(`/leads/quotes/${id}`);
export const updateQuoteStatus = (id, status) => api.patch(`/leads/quotes/${id}/status`, { status });
export const duplicateQuote = (id) => api.post(`/leads/quotes/${id}/duplicate`);
export const reviseQuote = (id) => api.post(`/leads/quotes/${id}/revise`);
export const searchApprovers = (query) => api.get("/leads/quotes/search-approvers", { query });
export const sendToApprover = (id, payload) => api.post(`/leads/quotes/${id}/send-to-approver`, payload);
export const addReviewComment = (id, comment) => api.post(`/leads/quotes/${id}/review-comment`, { comment });

export const GetCampaings = (page, limit) => api.get("/irp/finance/campaign/view", { page, limit });
export const uploadQuotePdf = async (quoteId, formData) => {
  const { data } = await axiosInstance.post(
    `/leads/quotes/upload-pdf/${quoteId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return data;
};

export const getQuotePdfLink = (quoteId) =>
  api.get(`/leads/quotes/pdf-link/${quoteId}`);
// ─── NEWA ─────────────────────────────────────────────────────
export const askNewa = async (payload, tenant_id) => {
  const queryText = typeof payload === 'string' ? payload : (payload?.question || payload?.message || payload?.raw_input || '');
  
  let moduleDomain = (typeof payload === 'object' && payload?.tenant_domain) ? payload.tenant_domain : 'crm';
  const queryLower = queryText.toLowerCase();
  if (queryText.includes('[Context: CRM') || queryLower.includes('lead') || queryLower.includes('pipeline') || queryLower.includes('deal')) {
    moduleDomain = 'crm';
  } else if (queryText.includes('[Context: Finance')) {
    moduleDomain = 'finance';
  } else if (queryText.includes('[Context: Inventory')) {
    moduleDomain = 'inventory';
  } else if (queryText.includes('[Context: HR')) {
    moduleDomain = 'hr';
  }

  // Get user auth token & active tenantId from sessionStorage / localStorage
  const tokenStr = sessionStorage.getItem('kt-auth-react-v');
  const rawUserData = sessionStorage.getItem('kt-user-data') || localStorage.getItem('user');
  let apiToken = 'dev-token';
  let realTenantId = tenant_id;

  if (tokenStr) {
    try {
      const parsed = JSON.parse(tokenStr);
      apiToken = parsed['api_token'] || parsed['token'] || tokenStr;
    } catch (e) {
      apiToken = tokenStr;
    }
  }

  if (rawUserData) {
    try {
      const u = JSON.parse(rawUserData);
      const extractedTid = u.tenantId || u.activeTenant?.tenantId || u.communityId || u.tenantProfiles?.[0]?.tenantId;
      if (extractedTid) {
        realTenantId = extractedTid;
      }
    } catch (e) {}
  }

  const requestBody = {
    raw_input: queryText,
    message: queryText,
    role: "admin",
    tenant_id: realTenantId && realTenantId.length >= 10 ? realTenantId : tenant_id,
    tenantId: realTenantId && realTenantId.length >= 10 ? realTenantId : tenant_id,
    tenant_domain: moduleDomain,
    tenantDomain: moduleDomain,
    conversation_id: typeof payload === 'object' ? payload?.conversation_id : null,
  };





  try {
    // Call port 8001 AskNEWA server directly
    const response = await axios.post(`${ASKNEWA_URL}/api/chat/`, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`,
        'x-platform': 'irp'
      },
      timeout: 60000
    });

    const data = response.data;
    let answerText = data.reply || data.answer || data.response || data.message || (typeof data === 'string' ? data : '');

    // Append structured sections (Recommendations, Action Plans, Lists)
    if (data.sections && Array.isArray(data.sections) && data.sections.length > 0) {
      let formattedSections = '';
      data.sections.forEach(sec => {
        if (sec.heading) {
          formattedSections += `\n\n### ${sec.heading}\n`;
        }
        if (sec.items && Array.isArray(sec.items)) {
          sec.items.forEach(item => {
            const cleanItem = String(item).replace(/\uFFFD|\u001F|\uFFFD/g, '').trim();
            if (cleanItem) formattedSections += `- ${cleanItem}\n`;
          });
        }
      });
      answerText += formattedSections;
    }

    return {
      answer: answerText.trim(),
      reply: answerText.trim(),
      conversationId: data.conversationId,
      data: data
    };


  } catch (err) {
    console.warn("[AskNEWA 8001 direct call failed, attempting fallback]:", err?.message);
    try {
      const response = await api.post("/irp/newa/ask", { question: queryText, tenant_id: realTenantId || moduleDomain });
      const fallbackText = response?.answer || response?.reply || response?.message || 'No response from server';

      return {
        answer: fallbackText,
        reply: fallbackText,
        data: response
      };
    } catch (fallbackErr) {
      throw err;
    }
  }
};

// ─── INVENTORY ────────────────────────────────────────────────
export const getInventoryHealth = (range) => api.get("/irp/inventory/helth", range ? { range } : {});
export const getRiskItems = (range) => api.get("/irp/inventory/riskitems", range ? { range } : {});
export const getInventorySuggestions = (range) => api.get("/irp/inventory/suggestions", range ? { range } : {});

// ─── PROJECT ────────────────────────────────────────────────
export const getProjects = () => api.get("/irp/project/view");
export const createProject = async (formData) => {
  try {
    const { data } = await axiosInstance.post("/project/create", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return data;
  } catch (error) {
    throw new Error("Failed to create project");
  }
};

// ─── COMMON USE ────────────────────────────────────────────────
export const AllUsers = () => api.get('/chat/allusers', { search: '' });
export const fetchMyCommunities = () => api.get('/communities/my-communities');
export const CommunityDetails = (query) => api.get(`/communities/search/${query}`);
export const createCampaign = async (datas) => {
  try {
    const { data } = await axiosInstance.post("/campaigns/create", datas, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return data;
  } catch (error) {}
};
export const getAccess = () => api.get('/irp/access-control');
export const getCommunityUsers = (communityId) => api.get(`/communities/users/${communityId}`);
export const getSystemConfig = () => api.get(`/system/configuration`);



